/**
 * auto-captions-dedup — repetition-hallucination guard for Whisper output.
 *
 * Whisper Tiny on q8 quantization (the WASM fallback path) is prone to looping
 * a single filler token or short phrase over noisy / music / low-SNR audio:
 *
 *     "что-то, что-то, что-то, что-то, что-то, что-то, …"      (period 1)
 *     "в этом году в этом году в этом году в этом году"          (period 3)
 *
 * This is a known Whisper failure mode (greedy decoder gets stuck on the
 * highest-probability token), NOT correct output. It is most visible on the
 * default Tiny model. WebGPU fp16 reduces it; this is the deterministic
 * post-process net that catches whatever still slips through, regardless of
 * model / backend.
 *
 * `collapseRepeats(words, opts)` walks the flat word array, finds a repeating
 * cycle of period 1..maxPeriod that repeats >= minRepeats times consecutively,
 * keeps a single cycle and drops the rest — extending the kept tail word's
 * `end` across the dropped run so caption timing stays monotonic.
 *
 * Conservative by design: a clean transcript never trips it (legit doubles /
 * triples are below the threshold), so `collapsed === 0` ⇒ array is returned
 * untouched and the caller leaves the original segments in place.
 *
 * Exposed as `window.__acDedup` (browser) and module.exports (node tests).
 */
(function () {
    'use strict';

    /* Normalise a token for equality: lowercase, strip surrounding punctuation
       and whitespace so "что-то", "что-то," and "Что-то" all compare equal.
       The internal hyphen in "что-то" is preserved (only edge punctuation is
       stripped) so it stays distinct from "что". */
    function norm(t) {
        return (t == null ? '' : String(t))
            .toLowerCase()
            .replace(/^[\s.,!?…"'`’“”»«()\[\]{}:;—–-]+/u, '')
            .replace(/[\s.,!?…"'`’“”»«()\[\]{}:;—–-]+$/u, '');
    }

    /**
     * @param {Array<{text:string,start:number,end:number}>} words
     * @param {{minRepeats?:number,maxPeriod?:number,keepCycles?:number}} [opts]
     * @returns {{words:Array, collapsed:number}} collapsed = words removed
     */
    function collapseRepeats(words, opts) {
        opts = opts || {};
        var minRepeats = opts.minRepeats || 4;  // cycle must repeat >= this many times
        var maxPeriod  = opts.maxPeriod  || 3;  // detect up to 3-word phrase loops
        var keepCycles = opts.keepCycles || 1;  // cycles preserved per collapsed run
        if (!Array.isArray(words) || words.length < minRepeats * 1) {
            return { words: words, collapsed: 0 };
        }

        var keys = new Array(words.length);
        for (var x = 0; x < words.length; x++) keys[x] = norm(words[x] && words[x].text);

        var out = [];
        var collapsed = 0;
        var i = 0, n = words.length;

        while (i < n) {
            var bestPeriod = 0, bestReps = 0, bestSpan = 0;

            for (var p = 1; p <= maxPeriod; p++) {
                if (i + p > n) break;
                // Require every token of the first cycle to be non-empty — a run
                // of blanks isn't a hallucination worth collapsing.
                var blank = false;
                for (var c = 0; c < p; c++) { if (!keys[i + c]) { blank = true; break; } }
                if (blank) continue;

                var reps = 1;
                for (;;) {
                    var base = i + reps * p;
                    if (base + p > n) break;
                    var same = true;
                    for (var q = 0; q < p; q++) {
                        if (keys[base + q] !== keys[i + q]) { same = false; break; }
                    }
                    if (!same) break;
                    reps++;
                }

                // Prefer the period that covers the longest run (reps*p). Ties go
                // to the smaller period (loop entered first).
                if (reps >= minRepeats && reps * p > bestSpan) {
                    bestPeriod = p; bestReps = reps; bestSpan = reps * p;
                }
            }

            if (bestPeriod && bestReps >= minRepeats) {
                var runLen = bestPeriod * bestReps;
                var keep   = Math.min(runLen, bestPeriod * keepCycles);
                for (var k = 0; k < keep; k++) out.push(words[i + k]);

                // Extend the last kept word's end across the dropped run so the
                // caption timeline has no gap where the loop used to be.
                var lastKept = out[out.length - 1];
                var runEnd   = words[i + runLen - 1];
                if (lastKept && runEnd &&
                    typeof runEnd.end === 'number' && typeof lastKept.end === 'number' &&
                    runEnd.end > lastKept.end) {
                    lastKept.end = runEnd.end;
                }

                collapsed += (runLen - keep);
                i += runLen;
            } else {
                out.push(words[i]);
                i++;
            }
        }

        return { words: out, collapsed: collapsed };
    }

    var api = { collapseRepeats: collapseRepeats, _norm: norm };

    if (typeof window !== 'undefined') window.__acDedup = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
